import { reactive, computed, getCurrentInstance, onMounted } from "vue";
import { useEmployees } from "@/composables/employees/useEmployees";
import { usePositions } from "@/composables/positions/usePositions";
import { useFilters } from "@/composables/filters/useFilters";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { useEmployeeStore } from "@/store/pinia/employee";
import { recalculateEmployeeReadiness } from "@/api";
import { requestAccessPermissionSync } from "@/utils/refreshAccessPermission";
import { updateUserAvatarInStorage } from "@components/helper/localStorageHelper";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import {
  getPositionId,
  getListOfPositionsForAssignment,
  getListOfPositionsForPrepareAssignment,
} from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";

let instance = null;

export function useEmployeeViewState() {
  if (instance) return instance;
  const employees = useEmployees();
  const positions = usePositions();
  const { highlight } = useHighlightRow();
  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const state = reactive({
    filters: {
      values: getDefaultFiltersForKey("organizationEmployees"),
      searchText: "",
      showFilterDialog: false,
      key: "organizationEmployees",
      clearSelection: "",
      statusOptions: ["draft", "active", "terminated"],
      filterGroups: [
        // {
        //   label: "Vacancy",
        //   items: [
        //     { label: "No Filter", value: "no_filter" },
        //     { label: "Vacant", value: "vacant" },
        //   ],
        // },
        // {
        //   label: "Availability",
        //   items: [
        //     { label: "No Filter", value: "no_filter" },
        //     { label: "Availability 0.5 FTE", value: "avail_05_fte" },
        //     { label: "Availability 1 FTE", value: "avail_1_fte" },
        //   ],
        // },
        {
          label: "Access",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "No Access", value: "no_access" },
            { label: "Has Access", value: "has_access" },
          ],
        },
        {
          label: "Assignments",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Has Assignments", value: "has_assignment" },
            { label: "No Assignments", value: "no_assignment" },
          ],
        },
        {
          label: "Archived",
          items: [
            { label: "Do not Show", value: "no_archived" },
            { label: "Show", value: "show_archived" },
          ],
        },
      ],
    },
    archiveAction: null,
    loading: {
      table: false,
      assignmentWizard: false,
      relieveAssignment: false,
      employeeAssignment: false,
    },
    assignmentWizard: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
    },
    preparePosition: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
    },
    relieveAssignment: {
      show: false,
      currentPosition: "",
      confirmationPending: false,
      type: "",
    },
    activeEmployeeModal: {
      show: false,
      message: "",
    },
    terminateEmployeeModal: {
      show: false,
      message: "",
    },
    suspendEmployeeAccessModal: {
      show: false,
      message: "",
    },
    giveBackEmployeeAccessModal: {
      show: false,
      message: "",
    },
    restoreEmployeeAccessModal: {
      show: false,
      message: "",
    },
    addEmployeeModal: {
      show: false,
      message: "",
    },
    changePictureModal: {
      show: false,
      avatarValue: null,
      croppedImage: null,
    },
    giveAccessModal: {
      show: false,
      dataType: "step1",
      existingUser: null,
      showEmailLoadingIcon: false,
      access_level: null,
    },
    giveBackAccessModal: {
      show: false,
    },
    reactivateEmployeeModal: {
      show: false,
      message: "",
    },
    resendInviteModal: {
      show: false,
    },
    cancelInviteModal: {
      show: false,
    },
    deleteEmployeeModal: {
      show: false,
      message: "",
    },
    archiveEmployeeModal: {
      show: false,
      message: "",
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalEmployeeChatter: 0,
      page: 1,
    },
    table: {
      selectedEmployees: [],
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    currentRow: null,
    assignmentPosition: {
      show: false,
      currentPosition: "",
    },
    paginationRoleModel: {
      page: 1,
      total: 0,
      perPage: 10,
    },
    searchMode: false,
    rolesList: [],
    responsibityList: [],
    assignmentItems: [],
    sanitizedItems: [],
    showRoleToPosition: false,
    attachmentId: null,
    unmatchedResponsibilities: [],
    showAssignmentCreationFlag: false,
    primaryPositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
    },
    preparePositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
    },
    featureFlags: {
      employeeAvailabilitySelector: true, // Default value
    },
  });
  const hasSelectedEmployees = computed(() => state.table.selectedEmployees.length > 0);

  const loadFiltersOnReload = () => {
    const savedFilters = filter.getFilters(state.filters.key); // Fetch filters from Vuex, localStorage, or API
    if (savedFilters && savedFilters.length > 0) {
      state.filters.values = savedFilters;
    }
  };

  const STATUS_FILTER_LABELS = {
    draft: "Draft",
    active: "Active",
    terminated: "Terminated",
  };

  const getFilterDisplayLabel = (value) => {
    if (!value) return value;
    if (STATUS_FILTER_LABELS[value]) {
      return STATUS_FILTER_LABELS[value];
    }
    for (const group of state.filters.filterGroups) {
      const item = (group.items || []).find((entry) => entry.value === value);
      if (item) {
        return item.label;
      }
    }
    return value;
  };

  const getFilterDisplayLabels = (values) => {
    return (values || []).map((value) => getFilterDisplayLabel(value));
  };

  const getFilterValueFromLabel = (labelOrValue) => {
    if (!labelOrValue) return labelOrValue;
    if ((state.filters.values || []).includes(labelOrValue)) {
      return labelOrValue;
    }
    const statusValue = Object.entries(STATUS_FILTER_LABELS).find(
      ([, label]) => label === labelOrValue
    );
    if (statusValue) {
      return statusValue[0];
    }
    for (const group of state.filters.filterGroups) {
      const item = (group.items || []).find((entry) => entry.label === labelOrValue);
      if (item) {
        return item.value;
      }
    }
    return labelOrValue;
  };

  const applyEmployeeFilterValues = () => {
    let archivedFilterApplied = false;
    state.filters.values.forEach((el) => {
      if (el == "draft" || el == "active" || el == "terminated") {
        employees.addEmployeeFilter("status", el);
      }
      if (el == "show_archived" || el == "no_archived") {
        archivedFilterApplied = true;
        if (el == "show_archived") {
          employees.addEmployeeFilter("archived", true);
        } else {
          employees.addEmployeeFilter("archived", false);
        }
      }
      if (el == "no_access") {
        employees.addEmployeeFilter("has_access", false);
      }
      if (el == "has_access") {
        employees.addEmployeeFilter("has_access", true);
      }
      if (el == "no_assignment") {
        employees.addEmployeeFilter("has_assignment", false);
      }
      if (el == "has_assignment") {
        employees.addEmployeeFilter("has_assignment", true);
      }
    });
    if (!archivedFilterApplied) {
      employees.addEmployeeFilter("archived", false);
    }
  };

  const applyFilters = (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    employees.clearEmployeeDynamicFilters();
    employees.setPaginationDataOffset(0);
    applyEmployeeFilterValues();

    // Re-apply search filters if they exist
    if (state.filters.searchText && state.filters.searchText.trim()) {
      const stringToArray = state.filters.searchText.split(" ");
      if (stringToArray.length == 2) {
        employees.addEmployeeFilter("first_name", stringToArray[0]);
        employees.addEmployeeFilter("last_name", stringToArray[1]);
      } else {
        employees.addEmployeeFilter("first_name", stringToArray[0]);
      }
    }

    state.filters.showFilterDialog = false;
    employees.setPaginationDataOffset(0);
    employees.loadAllEmployees();
  };

  const handleEmployeeSort = (sortData) => {
    const finalSortField = sortData.descending ? `-${sortData.field}` : `${sortData.field}`;
    employees.setOrderingDataField(finalSortField);
    employees.loadAllEmployees();
  };

  const removeFilters = (removedFilters) => {
    const removedValue = getFilterValueFromLabel(
      typeof removedFilters === "object" ? removedFilters.value : removedFilters
    );
    state.filters.values = state.filters.values.filter((value) => value !== removedValue);
    employees.clearEmployeeDynamicFilters();
    employees.setPaginationDataOffset(0);
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedValue;
    applyFilters(
      state.filters.values.map((value) => ({
        label: getFilterDisplayLabel(value),
        value,
      }))
    );
  };

  const removeAllFiltersAndReload = async () => {
    employees.clearEmployeeDynamicFilters();
    employees.addEmployeeFilter("archived", false);
    employees.setPaginationDataOffset(0);

    // ER-11195: "Clear all" restores the documented defaults (e.g. "No
    // Archived") from the central registry instead of an unfiltered list.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    state.filters.searchText = "";

    await employees.loadAllEmployees();
  };

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    // Always clear existing search filters first
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
    // Only add search filters and load employees if there's actually a search term
    if (string && string.trim() !== "") {
      employees.addEmployeeFilter("name", string);
    }
    employees.setPaginationDataOffset(0);
    state.filters.searchText = string;
    await employees.loadAllEmployees();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
    // if (string.length > 0) {
    // } else {
    //   clearSearch();
    // }
  };

  const handleRequest = async ({ page, itemsPerPage }) => {
    const hasArchivedFilter = state.filters.values.some(
      (el) => el == "show_archived" || el == "no_archived"
    );
    if (!hasArchivedFilter) {
      employees.addEmployeeFilter("archived", false);
    }
    employees.setPaginationDataLimit(itemsPerPage);
    employees.setPaginationDataOffset((page - 1) * itemsPerPage);
    await employees.loadAllEmployees();
  };

  const currentEmployee = computed(
    () => state.assignmentWizard.currentEmployee || state.relieveAssignment.currentEmployee
  );

  const getEmployeeCardProps = (employee) => {
    // Check feature flag for availability selector
    const isAvailabilitySelectorEnabled = state.featureFlags.employeeAvailabilitySelector;

    const hideAvailabilitySelector =
      !isAvailabilitySelectorEnabled || employee?.primary_position || false;

    return {
      firstName: employee.first_name,
      lastName: employee.last_name,
      // ------------------------- Commenting for ticket ER-5362 ------------------------------//

      // position:
      //   employee?.primary_positions?.length > 0
      //     ? employee.primary_positions[0].name
      //     : "",
      // position: employee.title,

      photoUrl: employee.avatar,
      availability: employee.availability,
      showAvailability: true,
      nameUrl: `employees/${employee.id}/pathtoreadiness`,
      showLinkUnderline: false,
      hideAvailabilitySelector: hideAvailabilitySelector,
      isAllocated: employee?.primary_position || false,
      emitFunctionToComposable: true,
    };
  };

  const getEmployeeAssigmentTagProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (!positionData?.occupied_by) {
        return "vacant";
      } else if (positionData?.occupied_by) {
        return "occupied";
      } else if (positionData?.blocked) {
        return "blocked";
      } else {
        return "not allowed";
      }
    },
    getBasicInfo: (item) => ({
      employeeId: item.id,
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
    }),

    getPositionInfo: (item) => ({
      positionCode: item.primary_position?.code_str?.split(" ")[0] || null,
      positionName: item.primary_position?.name,
      effective_date: item.primary_position?.effective_on,
      rolesCount: item.primary_position?.roles_count,
      responsibilityCount: item.primary_position?.responsibilities_count,
      fte: item.primary_position?.allocation || null,
      readiness: item.primary_readiness || 0,
      employeeLoading: item.primary_position?.employee_loading === 1 ? false : true,
    }),

    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.primary_position
          ? getEmployeeAssigmentTagProps.getTypeAssignmentTag(item.primary_position)
          : "no primary assignment",
    }),
  };
  const getEmployeePrepareAssignmentCardProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (positionData.occupied_by) {
        return "occupied";
      } else if (positionData.occupied_by === null) {
        return "vacant";
      } else if (positionData.status === "inactive") {
        return "inactive";
      } else {
        return "not allowed";
      }
    },
    getOccupiedByInfo: (item) => ({
      occupiedList: item?.preparing_for_positions?.[0]?.occupied_by
        ? [
            {
              firstName: item.preparing_for_positions[0].occupied_by.first_name,
              lastName: item.preparing_for_positions[0].occupied_by.last_name,
              photoUrl: item.preparing_for_positions[0].occupied_by.avatar,
              addHoverEffect: true,
            },
          ]
        : [],
    }),
    getEmployeeInfo: (item) => ({
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
      nameUrl: `employees/${item.id}/pathtoreadiness`,
    }),

    getPositionInfo: (item) => ({
      positionCode: item.preparing_for_positions?.[0]?.code_str?.split(" ")[0] || null,
      positionName: item.preparing_for_positions?.[0]?.name,
      effective_date: item.preparing_for_positions?.[0]?.effective_on,
      fte:
        item.preparing_for_positions?.length > 0
          ? item.preparing_for_positions[0].allocation
          : null,
      roles: item.preparing_for_positions?.[0]?.roles_count || 0,
      responsibilities: item.preparing_for_positions?.[0]?.responsibilities_count || 0,
      readiness: item.preparing_for_readiness || 0,
      employeeLoading: item.preparing_for_positions?.[0]?.employee_loading === 0 ? false : true,
    }),
    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.preparing_for_positions?.length > 0
          ? getEmployeePrepareAssignmentCardProps.getTypeAssignmentTag(
              item.preparing_for_positions[0]
            )
          : "no prepare assignment",
    }),
  };

  const getStateBadgeProps = (employee) => {
    return {
      access: getEmployeeAccess(employee?.access),
      status: employee.status,
      archived: employee?.archived || false,
      user: employee?.user || null,
      invitedDate: employee?.user?.date_of_invite || null,
    };
  };
  const getEmployeeAccess = (access) => {
    if (access?.is_suspended) {
      return "suspended";
    } else {
      return access ? access.employee_status : "no-access";
    }
  };

  const checkUserEmail = async ({ accountId, email }) => {
    state.giveAccessModal.showEmailLoadingIcon = true;
    const response = await employees.checkEmailUser({ accountId, email });
    state.giveAccessModal.showEmailLoadingIcon = false;
    if (response.blocked) {
      state.giveAccessModal.dataType = "emailBlocked";
    } else if (response.employee) {
      state.giveAccessModal.dataType = "emailUsed";
      state.giveAccessModal.existingUser = response.used_by;
    } else if (response.access && response.user && !response.employee) {
      const employeeName = (
        employees.getCurrentEmployee()?.first_name + employees.getCurrentEmployee()?.last_name
      )
        .toLowerCase()
        .replace(/\s/g, "");

      if (response.used_by?.toLowerCase().replace(/\s/g, "") === employeeName) {
        state.giveAccessModal.dataType = "details_email_user";
      } else {
        state.giveAccessModal.dataType = "accessedEmail";
      }
      state.giveAccessModal.existingUser = response.used_by;
      if (response.access_level === "user") {
        state.giveAccessModal.access_level = "app-User";
      } else if (response.access_level === "co-owner") {
        state.giveAccessModal.access_level = "co-Owner";
      } else {
        state.giveAccessModal.access_level = response.access_level;
      }
      // state.giveAccessModal.dataType = "accessedEmail";
    } else {
      state.giveAccessModal.dataType = "step2";
    }
  };

  const giveAccessToEmployee = async ({ accountId, payload }) => {
    proxy.$loader.setAppLoading(true);

    try {
      await employees.giveAccessToEmployee({ accountId, payload });
      await employees.loadAllEmployees();
      requestAccessPermissionSync(proxy.$root);
      proxy.$loader.setAppLoading(false);
      state.giveAccessModal.show = false;
      state.giveBackAccessModal.show = false;
    } catch (error) {
      proxy.$loader.setAppLoading(false);

      let errorMessage = "An error occurred while giving access to the employee.";

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.account_permission?.non_field_errors) {
          errorMessage =
            "You can't give access to the owner's email. Only the account owner can do that.";
        }
      }

      proxy.$notifications?.error(errorMessage);
    }
  };
  const loadAccountDetails = async () => {
    requestAccessPermissionSync(proxy.$root);
  };

  const openAssignmentWizard = () => {
    getListOfPrimaryPositions();
    state.assignmentWizard.show = true;
    state.assignmentWizard.selectedPosition = null;
  };
  const openAssignmentPosition = async (position, accountID) => {
    console.log("position: ", position);
    console.log("accountID: ", accountID);
    // proxy.$loader.show();
    // state.assignmentPosition.show = true;

    // // Get the position data with attachments from allPositions
    // const positionData = position?.primary_positions[0];
    // const fullPositionData = await getPositionId(
    //   accountID,
    //   positionData?.position
    // );

    // // Set currentPosition with attachments from the full position data
    // state.assignmentPosition.currentPosition = {
    //   ...positionData,
    //   ...fullPositionData?.data,
    //   attachments: fullPositionData?.data?.attachments || [],
    // };

    // state.rolesList = await positions.getRolesById({ accountId: accountID });
    // state.responsibityList = await positions.getResponsibilityByAttachRoles({
    //   accountId: accountID,
    // });
    // state.sanitizedItems = getSanitizedItems();
    // state.assignmentItems = paginatedAttachedRoles(state.sanitizedItems, 1);
    // proxy.$loader.hide();
  };
  const openPreparePositionDialog = async (position, accountID) => {
    proxy.$loader.show();
    state.assignmentPosition.show = true;

    // Get the position data with attachments from allPositions
    const positionData = position?.preparing_for_positions[0];
    const fullPositionData = await getPositionId(accountID, positionData?.position);

    // Set currentPosition with attachments from the full position data
    state.assignmentPosition.currentPosition = {
      ...positionData,
      ...fullPositionData?.data,
      attachments: fullPositionData?.data?.attachments || [],
    };

    state.rolesList = await positions.getRolesById({ accountId: accountID });
    state.responsibityList = await positions.getResponsibilityByAttachRoles({
      accountId: accountID,
    });
    state.sanitizedItems = getSanitizedItems();
    state.assignmentItems = paginatedAttachedRoles(state.sanitizedItems, 1);
    proxy.$loader.hide();
  };

  const getSanitizedItems = () => {
    let roleList = [];
    if (state.rolesList && state.rolesList.length > 0) {
      roleList = state.rolesList.map((el) => {
        return {
          id: el.id,
          parentId: -1,
          status: el.status,
          archived: el.archived,
          data: {
            type: "role",
            code: el.code + "",
            name: el.name,
            status: el.status,
          },
          info: el,
          attachedTo: {
            data: el.attachments,
          },
          types: "rolePosition",
          assignment: Object.values(el.attachments_count).reduce((sum, count) => sum + count, 0),
        };
      });
      const roleData = getMatchedData(
        roleList,
        state.assignmentPosition.currentPosition?.attachments ?? []
      );
      const matchedRoleData = transformData(roleData);

      const itemsToAdd = [];
      let responsibilitiesCopy = [];

      if (state.responsibityList?.length > 0) {
        responsibilitiesCopy = state.responsibityList.map((el) => {
          const typeCountRes = el.attachments.reduce((acc, att) => {
            acc[att.type] = (acc[att.type] || 0) + 1;
            return acc;
          }, {});
          el.attachments = Object.entries(typeCountRes).map(([type, quantity]) => ({
            type,
            quantity,
          }));
          return {
            id: el.id,
            realId: el.id,
            parentId: -1,
            status: el.status,
            archived: el.archived,
            data: {
              type: "responsibility",
              ...el,
            },
            types: "responsibilityPosition",
            attachedTo: { data: el.attachments },
            assignment: Object.values(el.attachments_count).reduce((sum, count) => sum + count, 0),
          };
        });
      }

      matchedRoleData.forEach((el) => {
        const attachments =
          el.attachedToItems?.data?.filter((ra) => ra.type === "responsibility") || [];
        attachments.forEach((el2) => {
          const tempItem = responsibilitiesCopy.find((res) => res.id === el2.id);
          if (!tempItem) return;
          const tempItemCopy = JSON.parse(JSON.stringify(tempItem));
          tempItemCopy.id = itemsToAdd.length + 400;
          tempItemCopy.parentId = el.id;
          itemsToAdd.push(tempItemCopy);
        });
      });

      return [...matchedRoleData, ...itemsToAdd];
    }

    return [];
  };

  const paginatedAttachedRoles = (sanitizedItems, page) => {
    if (!state.searchMode) {
      const tempArray = sanitizedItems;
      state.paginationRoleModel.page = page;
      let startIndex = (state.paginationRoleModel.page - 1) * 10;
      let withoutParentIdElements = 0;
      let finalIndex = 0;

      tempArray.forEach((el, index) => {
        if (index >= startIndex) {
          if (el.parentId == -1) {
            withoutParentIdElements++;
          }
          if (withoutParentIdElements <= 10) {
            finalIndex = index;
          }
        }
      });

      let finalArray = tempArray.slice(startIndex, finalIndex + 1);

      const withParentId = sanitizedItems.filter((el) => {
        return !finalArray.find((item) => item.parentId == el.parentId) && el.parentId > -1;
      });

      finalArray = finalArray.concat(withParentId);
      return finalArray;
    } else {
      return sanitizedItems;
    }
  };

  const getMatchedData = (data, attachments) => {
    const attachmentIds = attachments.map((att) => att.id);
    return data.filter((item) => attachmentIds.includes(item.id));
  };

  const transformData = (input) => {
    return input.map((item) => {
      const transformedItem = {
        id: item.id,
        parentId: item.parentId,
        archived: item.archived,
        data: {
          type: item.data.type,
          code: item.data.code ? item.data.code : "null",
          name: item.data.name,
          status: item?.data?.status,
        },
        attachedTo: {
          data: [],
        },
        attachedToItems: {
          ...item.attachedTo,
        },
        assignment: item.assignment,
        status: item.status,
        types: item.types,
        dashboard: item.dashboard,
        info: item.info,
      };
      const typeCountMap = item.attachedTo.data.reduce((acc, att) => {
        acc[att.type] = (acc[att.type] || 0) + 1;
        return acc;
      }, {});
      transformedItem.attachedTo.data = Object.entries(typeCountMap).map(([type, quantity]) => ({
        type,
        quantity,
      }));
      return transformedItem;
    });
  };

  const openPreparePosition = () => {
    getListOfPreparePositions();
    state.preparePosition.show = true;
    state.preparePosition.selectedPosition = null;
  };

  const closeAssignmentWizard = () => {
    state.assignmentWizard.show = false;
    state.assignmentWizard.selectedPosition = null;
  };
  const closePreparePosition = () => {
    state.preparePosition.show = false;
    state.preparePosition.selectedPosition = null;
  };

  const openRelieveAssignment = (employee, position) => {
    state.relieveAssignment.currentPosition = position;
    state.relieveAssignment.show = true;
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    employees.setCurrentEmployee(null);
    state.relieveAssignment.confirmationPending = false;
  };

  const relievePrimaryPositionFromEmployee = async (accountID, data) => {
    proxy.$loader.setAppLoading(true);
    const employee_id = employees.getCurrentEmployee()?.id;
    const payload = {
      effective_to: data,
    };
    try {
      await employees.removePositionAssignmentToEmployee({
        accountId: accountID,
        assignment_id: state.relieveAssignment.currentPosition.id,
        employee_id: employee_id,
        data: payload,
      });
    } catch (error) {
      proxy.$loader.setAppLoading(false);
      const responseData = error?.response?.data;
      const errorMessage =
        (Array.isArray(responseData) && responseData.join(" ")) ||
        responseData?.detail ||
        (typeof responseData === "string" && responseData) ||
        "Failed to relieve the position assignment. Please try again.";
      proxy.$notifications?.error(errorMessage);
      return;
    }
    await employees.loadAllEmployees();
    await positions.loadAllPositions();
    await recalculateEmployeeReadiness(accountID, employee_id);
    state.relieveAssignment.show = false;
    employees.setCurrentEmployee(null);
    proxy.$loader.setAppLoading(false);
  };

  const handleEmployeeDropdownOperation = async (operation, employee) => {
    state.currentRow = employee;
    if (!operation || !employee) return;
    employees.setCurrentEmployee(employee);
    switch (operation.value) {
      case "activate_action":
        state.activeEmployeeModal.message = `${employees.getCurrentEmployee()?.first_name || ""} ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.activeEmployeeModal.show = true;
        break;
      case "delete_action":
        state.deleteEmployeeModal.message = `${employees.getCurrentEmployee()?.first_name || ""} ${
          employees.getCurrentEmployee().last_name || ""
        } is about to be Deleted. This action is irreversible.`;
        state.deleteEmployeeModal.show = true;
        break;
      case "edit_action":
        state.addEmployeeModal.show = true;
        break;
      case "change_picture_action":
        state.changePictureModal.show = true;
        break;
      case "give_access_action":
        state.giveAccessModal.show = true;
        break;
      case "suspend_access_action":
        state.suspendEmployeeAccessModal.show = true;
        state.suspendEmployeeAccessModal.message = `You are about to suspend access for ${
          employees.getCurrentEmployee()?.first_name || ""
        } ${employees.getCurrentEmployee()?.last_name || ""} .`;
        break;
      case "give_back_access_action":
        state.restoreEmployeeAccessModal.show = true;
        state.restoreEmployeeAccessModal.message = "You are about to restore access for";
        break;
      case "resend_invite_action":
        state.resendInviteModal.show = true;
        break;
      case "cancel_invite_action":
        state.cancelInviteModal.show = true;
        break;
      case "terminate_action":
        state.terminateEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.terminateEmployeeModal.show = true;
        break;
      case "reactivate_action":
        state.reactivateEmployeeModal.show = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archiveEmployeeModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archiveEmployeeModal.show = true;
        break;
      default:
        break;
    }
  };

  const handleEmployeeOperation = async (operation, accountId, payload = null) => {
    try {
      proxy.$loader.setAppLoading(true);
      if (operation == employees.operations.TERMINATE_EMPLOYEE) {
        const terminatedEmployee = employees.getCurrentEmployee();
        await employees.terminate({
          accountId: accountId,
          employee: terminatedEmployee,
          data: payload,
        });
        const filters = (state.filters.values || []).map((f) => {
          const value = typeof f === "object" ? f.value : f;
          return String(value || "").toLowerCase();
        });
        const showTerminated = filters.includes("terminated");
        const terminatedPatch = {
          status: "terminated",
          primary_position: null,
          primary_positions: [],
          preparing_for_positions: [],
        };
        if (terminatedEmployee?.id) {
          if (showTerminated) {
            employees.updateEmployeeInList(terminatedEmployee.id, terminatedPatch);
          } else {
            employees.removeEmployeeFromList(terminatedEmployee.id);
          }
        }
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees({ force: true });
        await recalculateEmployeeReadiness(accountId, terminatedEmployee?.id);
        state.terminateEmployeeModal.show = false;
      } else if (operation == employees.operations.ACTIVATE_EMPLOYEE) {
        await employees.activate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.activeEmployeeModal.show = false;
      } else if (operation == employees.operations.DELETE_EMPLOYEE) {
        await employees.deleteEmp({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.deleteEmployeeModal.show = false;
      } else if (operation == employees.operations.REACTIVATE_EMPLOYEE) {
        await employees.reactivate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.reactivateEmployeeModal.show = false;
      } else if (operation == employees.operations.EDIT_EMPLOYEE) {
        if (!payload) return;
        const newEmployee = await employees.saveEmployee({
          accountId,
          employee: payload,
          allData: employees.getCurrentEmployee(),
        });

        if (!payload?.id) {
          // For NEW employees, preserve existing filters but add search for the new employee
          if (newEmployee) {
            const { data } = newEmployee;

            // Redirect to path to readiness page for all new employees
            if (data?.id) {
              state.addEmployeeModal.show = false;
              proxy.$loader.setAppLoading(false);
              await proxy.$nextTick();
              proxy.$router.push(`/organization/employees/${data.id}/pathtoreadiness`);
              return;
            }

            state.addEmployeeModal.show = false;
            const existingFilters = [...state.filters.values]; // Preserve existing filters
            state.filters.searchText = `${data.first_name} ${data.last_name}`;

            // Clear and re-apply all filters including the new search
            employees.clearEmployeeDynamicFilters();
            employees.setPaginationDataOffset(0);

            state.filters.values = existingFilters;
            applyEmployeeFilterValues();

            // Add search filters for the new employee
            const stringToArray = state.filters.searchText.split(" ");
            if (stringToArray.length == 2) {
              employees.addEmployeeFilter("first_name", stringToArray[0]);
              employees.addEmployeeFilter("last_name", stringToArray[1]);
            } else {
              employees.addEmployeeFilter("first_name", stringToArray[0]);
            }

            await employees.loadAllEmployees();
            setTimeout(() => {
              highlight({ id: newEmployee.data.id, type: "Employees" });
            }, 500);
          }
        } else {
          // For existing employee edits, just reload without clearing filters
          state.addEmployeeModal.show = false;
          await employees.loadAllEmployees();
        }
      } else if (operation == employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        const newEmployee = {
          id: employees.getCurrentEmployee()?.id,
          first_name: employees.getCurrentEmployee()?.first_name,
          last_name: employees.getCurrentEmployee()?.last_name,
          title: employees.getCurrentEmployee()?.title,
          avatar: payload,
        };
        const response = await employees.saveEmployeeWithAvatar({
          accountId,
          employee: newEmployee,
        });

        // Update currentEmployee with the actual response data
        const currentEmp = employees.getCurrentEmployee();
        if (currentEmp && response?.data) {
          const updatedEmployee = {
            ...currentEmp,
            ...response.data, // Use the complete updated employee data from server
          };
          employees.setCurrentEmployee(updatedEmployee);
        }

        // Update localStorage if this is the logged-in user's employee
        if (response?.data && response?.data?.avatar !== undefined) {
          updateUserAvatarInStorage(response.data, response.data.avatar || "");
        }

        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
      } else if (operation == employees.operations.ARCHIVE_EMPLOYEE) {
        if (state.archiveAction === "archive") {
          await employees.archiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        } else if (state.archiveAction === "unarchive") {
          await employees.unArchiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        }
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await employees.loadAllEmployees();
        state.archiveEmployeeModal.show = false;
      }
      if (operation != employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        employees.setCurrentEmployee(null);
      }
    } catch (e) {
      console.error("error on employee operation: ", e);
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  };

  const selectEmployee = (employee) => {
    const index = state.table.selectedEmployees.findIndex((e) => e.id === employee.id);
    if (index === -1) {
      state.table.selectedEmployees.push(employee);
    } else {
      state.table.selectedEmployees.splice(index, 1);
    }
  };

  // Métodos para filtros
  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const onAssignPositionToEmployee = async ({ position, accountId }) => {
    proxy.$loader.setAppLoading(true);
    state.assignmentWizard.isAssigning = true; // Set loading state for employee assignment
    if (
      !employees.getCurrentEmployee()?.id ||
      !position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      return;
    }
    try {
      // Set the position object in primary_position as an object
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        // Ensure the position object has the correct structure matching API response format
        const positionWithCodeStr = {
          code_str: position.code,
          name: position.name,
          effective_on: position.effective_on,
          roles_count: null,
          responsibilities_count: null,
          allocation: position.allocation || 1,
          employee_loading: 0,
          status: "active",
          occupied_by: {
            first_name: currentEmployee.first_name,
            last_name: currentEmployee.last_name,
            avatar: currentEmployee.avatar,
          },
        };

        // Create a new employee object with the position set in primary_position (object)
        const updatedEmployee = {
          ...currentEmployee,
          primary_position: positionWithCodeStr,
        };

        // Update the current employee with the new primary_position
        employees.setCurrentEmployee(updatedEmployee);

        // Update the employee in allEmployees list by replacing old data with new data based on ID
        const allEmployees = [...employees.allEmployees.value]; // Create a new array to ensure reactivity
        const employeeIndex = allEmployees.findIndex((emp) => emp.id == currentEmployee.id);

        if (employeeIndex !== -1) {
          // Replace the old employee data with the new updated employee data
          allEmployees[employeeIndex] = updatedEmployee;

          // Use the store's setEmployees method to ensure proper reactivity
          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.assignmentWizard.show = false;
      }
      await employees.onAssignPositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position,
        accountID: accountId,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await employees.loadAllEmployees(false);
      await recalculateEmployeeReadiness(accountId, employees.getCurrentEmployee()?.id);
      // // Now fetch the updated employee from backend
      // const updatedEmployeeFromBackend = await employees.getEmployeeById({
      //   accountId,
      //   employeeId: employees.getCurrentEmployee().id,
      // });

      // if (updatedEmployeeFromBackend) {
      //   employees.setCurrentEmployee(updatedEmployeeFromBackend);
      //   const allEmployees = [...employees.allEmployees.value];
      //   const employeeIndex = allEmployees.findIndex(
      //     (emp) => emp.id == updatedEmployeeFromBackend.id
      //   );
      //   if (employeeIndex !== -1) {
      //     allEmployees[employeeIndex] = updatedEmployeeFromBackend;
      //     const employeeStore = useEmployeeStore();
      //     employeeStore.setEmployees(allEmployees);
      //   }
      // }
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.assignmentWizard.show = false;
      state.assignmentWizard.isAssigning = false; // Reset loading state
    }
    proxy.$loader.setAppLoading(false);
  };
  const onAssignPreparePositionToEmployee = async ({ obj, accountId }) => {
    proxy.$loader.setAppLoading(true);
    state.preparePosition.isAssigning = true; // Set loading state for prepare position assignment
    if (
      !employees.getCurrentEmployee()?.id ||
      !obj?.position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      return;
    }
    try {
      // Set the position object in preparing_for_positions array at index 0
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        // Ensure the position object has the correct structure with all required properties
        const positionWithCodeStr = {
          ...obj.position,
          code: obj.position.code_str,
          occupied_by: currentEmployee, // Position is now occupied by current employee
          status: "active", // Ensure status is active
          effective_on: obj.effective_date, // Set effective date
          allocation: obj.position.allocation || 1, // Default allocation
          roles_count: null, // Set roles count as null
          responsibilities_count: null, // Set responsibilities count as null
          employee_loading: 1,
        };

        // Create a new employee object with the position set in preparing_for_positions[0]
        const updatedEmployee = {
          ...currentEmployee,
          preparing_for_positions: [positionWithCodeStr],
        };

        // Update the current employee with the new preparing_for_positions
        employees.setCurrentEmployee(updatedEmployee);

        // Update the employee in allEmployees list by replacing old data with new data based on ID
        const allEmployees = [...employees.allEmployees.value]; // Create a new array to ensure reactivity
        const employeeIndex = allEmployees.findIndex((emp) => emp.id == currentEmployee.id);

        if (employeeIndex !== -1) {
          // Replace the old employee data with the new updated employee data
          allEmployees[employeeIndex] = updatedEmployee;
          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.preparePosition.show = false;
      }
      await employees.onAssignPreparePositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position: obj.position,
        effective_date: obj.effective_date,
        accountID: accountId,
        isNotify: obj.isNotify,
      });
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await employees.loadAllEmployees(false);
      await recalculateEmployeeReadiness(accountId, employees.getCurrentEmployee()?.id);
      // Now fetch the updated employee from backend
      // const updatedEmployeeFromBackend = await employees.getEmployeeById({
      //   accountId,
      //   employeeId: employees.getCurrentEmployee().id,
      // });
      // if (updatedEmployeeFromBackend) {
      //   employees.setCurrentEmployee(updatedEmployeeFromBackend);
      //   const allEmployees = [...employees.allEmployees.value];
      //   const employeeIndex = allEmployees.findIndex(
      //     (emp) => emp.id == updatedEmployeeFromBackend.id
      //   );
      //   if (employeeIndex !== -1) {
      //     allEmployees[employeeIndex] = updatedEmployeeFromBackend;
      //     const employeeStore = useEmployeeStore();
      //     employeeStore.setEmployees(allEmployees);
      //   }
      // }
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      state.preparePosition.isAssigning = false; // Reset loading state
    }
    proxy.$loader.setAppLoading(false);
  };

  const clearFilters = () => {
    state.filters = {
      status: "all",
      department: null,
      position: null,
    };
  };

  const clearSearch = () => {
    state.filters.searchText = "";
    // Clear search filters from dynamic filters
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
  };

  // Métodos para loading
  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };

  const sanitizedRoles = () => {
    if (state.rolesList && state.rolesList.length > 0) {
      const attachedResponsibilityIds = new Set(
        state.assignmentPosition.currentPosition.attachments.map((attachment) => attachment.id)
      );
      const filteredRoles = state.rolesList
        .filter((item) => !attachedResponsibilityIds.has(item.id))
        .filter((item) => item.status !== "retired");

      state.unmatchedResponsibilities = filteredRoles.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        name: item.name,
        status: item.status,
      }));
    } else {
      state.unmatchedResponsibilities = [];
    }
  };

  const openRoleToPosition = () => {
    state.showRoleToPosition = true;
    sanitizedRoles();
  };

  const handleItemSelected = (data) => {
    if (data && Object.prototype.hasOwnProperty.call(data, "id")) {
      state.attachmentId = data.id;
    }
  };

  const addHandleItemSelected = async ({ data1, accountId }) => {
    try {
      const role = {
        name: data1.name,
        account: accountId,
      };
      const data = await positions.createAttachRole({ accountId, role });
      const newRole = data;
      state.attachmentId = newRole.id;
      await attachedRole({ accountId });
    } catch (e) {
      console.log("e: ", e);
    }
  };

  const attachedRole = async ({ accountId }) => {
    try {
      const positionId = state.assignmentPosition.currentPosition?.position;
      const roleId = state.attachmentId;
      await positions.attachRole({ accountId, positionId, roleId });
      await reloadRoleData();
      state.sanitizedItems = getSanitizedItems();
      state.showRoleToPosition = false;
    } catch (e) {
      console.log("e: ", e);
    }
  };
  const getEmployeeChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await employees.employeeChatter(
        accountID,
        "employee",
        state.openChatterModal.page
      );
      if (response) {
        const chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalEmployeeChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };
  const openEmployeeChatterModal = async () => {
    state.openChatterModal.chatterData = [];
    await getEmployeeChatter();
  };
  const closeEmployeeChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalEmployeeChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };
  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getEmployeeChatter();
  };
  const onApplyDatePrepareFor = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrepareFor({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await employees.loadAllEmployees();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };
  const onApplyDatePrimaryAssignment = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrimaryAssignment({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await employees.loadAllEmployees();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const reloadRoleData = async () => {
    try {
      proxy.$loader.show();
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      // Reload the roles and responsibilities data
      state.rolesList = await positions.getRolesById({ accountId });
      state.responsibityList = await positions.getResponsibilityByAttachRoles({
        accountId,
      });

      // Update the current position with the latest data
      const currentEmployee = employees.getCurrentEmployee();

      // Determine if we're dealing with primary or prepare position
      const isPreparePosition = currentEmployee?.preparing_for_positions?.some(
        (pos) => pos.position === state.assignmentPosition.currentPosition?.position
      );

      if (isPreparePosition && currentEmployee?.preparing_for_positions?.[0]) {
        // Handle prepare position case
        const updatedPosition = await getPositionId(
          accountId,
          currentEmployee.preparing_for_positions[0].position
        );
        if (updatedPosition) {
          state.assignmentPosition.currentPosition = {
            ...currentEmployee.preparing_for_positions[0],
            ...updatedPosition?.data,
            attachments: updatedPosition?.data?.attachments || [],
          };
        }
      } else if (currentEmployee?.primary_positions?.[0]) {
        // Handle primary assignment case
        const updatedPosition = await getPositionId(
          accountId,
          currentEmployee.primary_positions[0].position
        );
        if (updatedPosition) {
          state.assignmentPosition.currentPosition = {
            ...currentEmployee.primary_positions[0],
            ...updatedPosition?.data,
            attachments: updatedPosition?.data?.attachments || [],
          };
        }
      }

      // Refresh the sanitized items   and assignment items
      state.sanitizedItems = getSanitizedItems();
      state.assignmentItems = paginatedAttachedRoles(state.sanitizedItems, 1);
      proxy.$loader.hide();
    } catch (e) {
      console.error("error refreshing assignment data: ", e);
    }
  };
  const getListOfPrimaryPositions = async (page = 1, searchTerm = "", isLoadMore = false) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    try {
      // Set loading state for pagination primary positions
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const primaryPositions = await getListOfPositionsForAssignment({
        accountId,
        limit,
        offset,
        name,
      });
      let newItems = primaryPositions.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        code_str: item.code_str,
        name: item.name,
        status: item.status,
      }));

      // Filter out positions if current employee has preparing_for_positions matching them
      if (currentEmployee?.preparing_for_positions?.length > 0) {
        const preparingCodeStr = currentEmployee.preparing_for_positions[0].code_str;
        newItems = newItems.filter((item) => item.code_str !== preparingCodeStr);
      }

      // If it's a load more request, append to existing items
      if (isLoadMore && state.primaryPositions.availablePositions) {
        state.primaryPositions.availablePositions = [
          ...state.primaryPositions.availablePositions,
          ...newItems,
        ];
      } else {
        // If it's initial load or search, replace items
        state.primaryPositions.availablePositions = newItems;
      }

      // Update pagination state
      state.primaryPositions.currentPage = page;
      state.primaryPositions.totalCount = primaryPositions.data.count || 0;
      // hasMoreData should be true if there are more records beyond current page
      state.primaryPositions.hasMoreData = page * limit < state.primaryPositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      // Clear loading state
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = false;
      }
    }
  };

  // Method to handle load more for pagination
  const loadMorePrimaryPositions = async () => {
    if (state.primaryPositions.isLoadingMore || !state.primaryPositions.hasMoreData) {
      return;
    }

    const nextPage = state.primaryPositions.currentPage + 1;
    await getListOfPrimaryPositions(nextPage, state.primaryPositions.currentSearchTerm, true);
  };

  // Method to handle search with pagination reset
  const searchPrimaryPositions = async (searchTerm) => {
    state.primaryPositions.currentSearchTerm = searchTerm;
    state.primaryPositions.currentPage = 1;
    state.primaryPositions.hasMoreData = true;
    await getListOfPrimaryPositions(
      state.primaryPositions.currentPage,
      state.primaryPositions.currentSearchTerm,
      false
    );
  };
  const getListOfPreparePositions = async (page = 1, searchTerm = "", isLoadMore = false) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    try {
      // Set loading state for pagination primary positions
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const preparePositions = await getListOfPositionsForPrepareAssignment({
        accountId,
        limit,
        offset,
        name,
      });

      let newItems = preparePositions.data.results.map((item) => ({
        ...item,
      }));

      // Filter out positions if current employee has preparing_for_positions matching them
      if (
        currentEmployee &&
        currentEmployee?.primary_position &&
        currentEmployee?.primary_position?.code_str
      ) {
        const primaryCodeStr = currentEmployee?.primary_position?.code_str;
        newItems = newItems.filter((item) => item.code_str !== primaryCodeStr);
      }

      // If it's a load more request, append to existing items
      if (isLoadMore && state.preparePositions.availablePositions) {
        state.preparePositions.availablePositions = [
          ...state.preparePositions.availablePositions,
          ...newItems,
        ];
      } else {
        // If it's initial load or search, replace items
        state.preparePositions.availablePositions = newItems;
      }

      // Update pagination state
      state.preparePositions.currentPage = page;
      state.preparePositions.totalCount = preparePositions.data.count || 0;
      // hasMoreData should be true if there are more records beyond current page
      state.preparePositions.hasMoreData = page * limit < state.preparePositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      // Clear loading state
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = false;
      }
    }
  };
  const loadMorePreparePositions = async () => {
    if (state.preparePositions.isLoadingMore || !state.preparePositions.hasMoreData) {
      return;
    }

    const nextPage = state.preparePositions.currentPage + 1;
    await getListOfPreparePositions(nextPage, state.preparePositions.currentSearchTerm, true);
  };

  // Method to handle search with pagination reset
  const searchPreparePositions = async (searchTerm) => {
    state.preparePositions.currentSearchTerm = searchTerm;
    state.preparePositions.currentPage = 1;
    state.preparePositions.hasMoreData = true;
    await getListOfPreparePositions(
      state.preparePositions.currentPage,
      state.preparePositions.currentSearchTerm,
      false
    );
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    // Reset the employee filters singleton to prevent persistence of search parameters across sessions
    // DO NOT APPLY THIS -> -> -> CAUSE PROBLEMS IN PAGINATION
    // resetEmployeeFilters();

    // Clear dynamic filters to prevent persistence of search parameters across sessions
    employees.clearEmployeeDynamicFilters();

    // Check feature flag for hiding archived filter
    const hideArchivedFilterEmployees = await proxy.$getFlag(
      "hide_archived_filter_employees",
      false
    );

    if (hideArchivedFilterEmployees) {
      // Remove archived filter from filterGroups
      state.filters.filterGroups = state.filters.filterGroups.filter(
        (group) => group.label !== "Archived"
      );
    }

    // Load feature flag for availability selector
    const employeeAvailabilitySelector = await proxy.$getFlag(
      "employee_availability_selector",
      true
    );
    state.featureFlags.employeeAvailabilitySelector = employeeAvailabilitySelector;
  };

  // Lifecycle hook to initialize filters on page reload
  onMounted(async () => {
    await initializeFilters();
    await proxy.$watchFlag("show-assignment-creation-progress");

    state.showAssignmentCreationFlag = proxy.flags["show-assignment-creation-progress"];
    // Note: loadFiltersOnReload() is now called explicitly in the component's onMounted
    // to ensure proper timing with filter application
  });

  instance = {
    state,
    hasSelectedEmployees,
    currentEmployee,
    openAssignmentWizard,
    openAssignmentPosition,
    openPreparePositionDialog,
    openPreparePosition,
    closeAssignmentWizard,
    closePreparePosition,
    openRelieveAssignment,
    closeRelieveAssignment,
    selectEmployee,
    updateFilter,
    clearFilters,
    setLoading,
    getEmployeeCardProps,
    getEmployeeAssigmentTagProps,
    relievePrimaryPositionFromEmployee,
    getStateBadgeProps,
    handleEmployeeDropdownOperation,
    handleEmployeeOperation,
    checkUserEmail,
    applyFilters,
    getFilterDisplayLabel,
    getFilterDisplayLabels,
    getFilterValueFromLabel,
    removeFilters,
    giveAccessToEmployee,
    onAssignPositionToEmployee,
    onAssignPreparePositionToEmployee,
    clearSearch,
    getEmployeePrepareAssignmentCardProps,
    getSanitizedItems,
    paginatedAttachedRoles,
    openRoleToPosition,
    handleItemSelected,
    addHandleItemSelected,
    attachedRole,
    sanitizedRoles,
    getEmployeeChatter,
    openEmployeeChatterModal,
    closeEmployeeChatterModal,
    requestMoreChatter,
    onApplyDatePrepareFor,
    onApplyDatePrimaryAssignment,
    getEmployeeAccess,
    reloadRoleData,
    handleEmployeeSort,
    handleSearch,
    handleRequest,
    getListOfPrimaryPositions,
    loadMorePrimaryPositions,
    searchPrimaryPositions,
    getListOfPreparePositions,
    loadMorePreparePositions,
    searchPreparePositions,
    removeAllFiltersAndReload,
    initializeFilters,
    loadFiltersOnReload,
  };
  return instance;
}

export function resetEmployeeViewState() {
  instance = null;
}
